import { Command } from '@/components/shared';

export type CommandContext = {
    navigate: (path: string) => void;
    openCreateUserModal?: () => void;
    openInviteUserModal?: () => void;
    openCreateTeamModal?: () => void;
    openCreateOrgModal?: () => void;
    fetchDashboardData?: () => void;
    setDays?: (days: number) => void;
    fetchTeamsData?: () => void;
    fetchHRVData?: () => void;
    fetchSleepData?: () => void;
    fetchActivityData?: () => void;
    refreshUserInfo?: () => void;
    triggerDataSync?: () => void;
    setShowDebugPanel?: () => void;
    fetchTeamData?: () => void;
    athletePageContext?: {
        currentUrl: string;
    };
};

export const generateCommands = (context: CommandContext): Command[] => {
    const commands: Command[] = [];

    commands.push(
        {
            id: 'invite-user',
            label: 'Invite User',
            icon: 'UserPlus',
            description: 'Send an email invitation to join a team',
            keywords: ['add', 'new', 'user', 'invite', 'member', 'email'],
            action: () => {
                if (context.openInviteUserModal) {
                    context.openInviteUserModal();
                }
            },
            shortcut: ['cmd', 'shift', 'u'],
            roles: ['admin', 'coach']
        },
        {
            id: 'create-team',
            label: 'Create Team',
            icon: 'Trophy',
            description: 'Create a new team',
            keywords: ['add', 'new', 'team'],
            action: () => {
                if (context.setShowCreateTeamModal) {
                    context.setShowCreateTeamModal(true);
                } else {
                    context.navigate('/admin-dashboard?tab=teams');
                }
            },
            shortcut: ['cmd', 'shift', 't'],
            roles: ['admin']
        },
        {
            id: 'create-organization',
            label: 'Create Organization',
            icon: 'Building2',
            description: 'Add a new organization',
            keywords: ['add', 'new', 'organization'],
            action: () => {
                if (context.openCreateOrgModal) {
                    context.openCreateOrgModal();
                }
            },
            shortcut: ['cmd', 'shift', 'o'],
            roles: ['admin']
        },
        {
            id: 'navigate-overview',
            label: 'Go to Admin Overview',
            icon: 'LayoutDashboard',
            description: 'View dashboard overview',
            keywords: ['overview', 'dashboard', 'admin'],
            action: () => context.navigate('/admin-dashboard?tab=overview'),
            roles: ['admin']
        },
        {
            id: 'navigate-users',
            label: 'Go to Users Management',
            icon: 'Users',
            description: 'Manage users',
            keywords: ['users', 'members', 'admin'],
            action: () => context.navigate('/admin-dashboard?tab=users'),
            roles: ['admin']
        },
        {
            id: 'navigate-teams',
            label: 'Go to Teams Management',
            icon: 'Trophy',
            description: 'Manage teams',
            keywords: ['teams', 'groups'],
            action: () => context.navigate('/admin-dashboard?tab=teams'),
            roles: ['admin']
        },
        {
            id: 'navigate-organizations',
            label: 'Go to Organizations',
            icon: 'Building2',
            description: 'Manage organizations',
            keywords: ['organizations', 'orgs'],
            action: () => context.navigate('/admin-dashboard?tab=organizations'),
            roles: ['admin']
        },
        {
            id: 'refresh-admin-data',
            label: 'Refresh Admin Data',
            icon: 'RefreshCw',
            description: 'Reload dashboard data',
            keywords: ['refresh', 'reload', 'admin'],
            action: () => {
                if (context.fetchDashboardData) {
                    context.fetchDashboardData();
                } else {
                    context.navigate('/admin-dashboard');
                }
            },
            roles: ['admin']
        }
    );

    commands.push(
        {
            id: 'navigate-team-overview',
            label: 'Go to Team Overview',
            icon: 'LayoutDashboard',
            description: 'View team summary',
            keywords: ['overview', 'team', 'coach'],
            action: () => context.navigate('/coach-dashboard?view=overview'),
            roles: ['coach', 'admin']
        },
        {
            id: 'navigate-analytics',
            label: 'Go to Analytics',
            icon: 'TrendingUp',
            description: 'View analytics dashboard',
            keywords: ['analytics', 'charts'],
            action: () => context.navigate('/coach-dashboard?view=analytics'),
            roles: ['coach', 'admin']
        },
        {
            id: 'navigate-rankings',
            label: 'Go to Rankings',
            icon: 'Trophy',
            description: 'View athlete rankings',
            keywords: ['rankings', 'leaderboard'],
            action: () => context.navigate('/coach-dashboard?view=rankings'),
            roles: ['coach', 'admin']
        },
        {
            id: 'navigate-athletes',
            label: 'Go to Athletes',
            icon: 'Users',
            description: 'View athlete roster',
            keywords: ['athletes', 'roster'],
            action: () => context.navigate('/coach-dashboard?view=athletes'),
            roles: ['coach', 'admin']
        },
        {
            id: 'navigate-chat',
            label: 'Open AI Chat',
            icon: 'Bot',
            description: 'Chat with AI',
            keywords: ['chat', 'ai'],
            action: () => context.navigate('/coach-dashboard?view=chat'),
            roles: ['coach', 'admin']
        },
        {
            id: 'refresh-team-data',
            label: 'Refresh Team Data',
            icon: 'RefreshCw',
            description: 'Reload team metrics',
            keywords: ['refresh', 'reload'],
            action: () => {
                if (context.fetchTeamsData) {
                    context.fetchTeamsData();
                } else {
                    context.navigate('/coach-dashboard');
                }
            },
            roles: ['coach', 'admin']
        }
    );

    commands.push(
        {
            id: 'change-period-7',
            label: 'View Last 7 Days',
            icon: 'Calendar',
            description: 'Change period to last 7 days',
            keywords: ['period', 'week', '7 days'],
            action: () => {
                if (context.setDays) {
                    context.setDays(7);
                } else {
                    context.navigate('/dashboard');
                }
            },
            roles: ['athlete', 'coach', 'admin']
        },
        {
            id: 'change-period-14',
            label: 'View Last 14 Days',
            icon: 'Calendar',
            description: 'Change period to last 2 weeks',
            keywords: ['period', '14 days'],
            action: () => {
                if (context.setDays) {
                    context.setDays(14);
                } else {
                    context.navigate('/dashboard');
                }
            },
            roles: ['athlete', 'coach', 'admin']
        },
        {
            id: 'change-period-30',
            label: 'View Last 30 Days',
            icon: 'Calendar',
            description: 'Change period to last 30 days',
            keywords: ['period', 'month', '30 days'],
            action: () => {
                if (context.setDays) {
                    context.setDays(30);
                } else {
                    context.navigate('/dashboard');
                }
            },
            roles: ['athlete', 'coach', 'admin']
        },
        {
            id: 'change-period-60',
            label: 'View Last 60 Days',
            icon: 'Calendar',
            description: 'Change period to last 60 days',
            keywords: ['period', '60 days'],
            action: () => {
                if (context.setDays) {
                    context.setDays(60);
                } else {
                    context.navigate('/dashboard');
                }
            },
            roles: ['athlete', 'coach', 'admin']
        },
        {
            id: 'change-period-90',
            label: 'View Last 90 Days',
            icon: 'Calendar',
            description: 'Change period to last 90 days',
            keywords: ['period', '90 days'],
            action: () => {
                if (context.setDays) {
                    context.setDays(90);
                } else {
                    context.navigate('/dashboard');
                }
            },
            roles: ['athlete', 'coach', 'admin']
        }
    );

    commands.push(
        {
            id: 'refresh-personal-data',
            label: 'Sync My Data',
            icon: 'RefreshCw',
            description: 'Trigger data sync',
            keywords: ['refresh', 'sync'],
            action: () => {
                if (context.triggerDataSync) {
                    context.triggerDataSync();
                } else {
                    context.navigate('/dashboard');
                }
            },
            roles: ['athlete', 'coach', 'admin']
        },
        {
            id: 'toggle-debug',
            label: 'Toggle Debug Panel',
            icon: 'Wrench',
            description: 'Show/hide debug info',
            keywords: ['debug', 'developer'],
            action: () => {
                if (context.setShowDebugPanel) {
                    context.setShowDebugPanel();
                } else {
                    context.navigate('/dashboard');
                }
            },
            roles: ['admin']
        }
    );

    commands.push(
        {
            id: 'team-info-overview',
            label: 'Team Info: Overview',
            icon: 'LayoutDashboard',
            description: 'View team overview',
            keywords: ['team', 'overview'],
            action: () => context.navigate('/team-information?tab=overview'),
            roles: ['coach', 'admin', 'athlete']
        },
        {
            id: 'team-info-codes',
            label: 'Team Info: Join Codes',
            icon: 'Key',
            description: 'View join codes',
            keywords: ['team', 'codes'],
            action: () => context.navigate('/team-information?tab=codes'),
            roles: ['coach', 'admin']
        },
        {
            id: 'team-info-members',
            label: 'Team Info: Members',
            icon: 'Users',
            description: 'View team members',
            keywords: ['team', 'members'],
            action: () => context.navigate('/team-information?tab=members'),
            roles: ['coach', 'admin', 'athlete']
        },
        {
            id: 'refresh-team-info',
            label: 'Refresh Team Information',
            icon: 'RefreshCw',
            description: 'Reload team data',
            keywords: ['refresh', 'team'],
            action: () => {
                if (context.fetchTeamData) {
                    context.fetchTeamData();
                } else {
                    context.navigate('/team-information');
                }
            },
            roles: ['coach', 'admin', 'athlete']
        }
    );

    if (context.athletePageContext) {
        const baseUrl = context.athletePageContext.currentUrl;
        commands.push(
            {
                id: 'athlete-detail-overview',
                label: 'Athlete: Overview',
                icon: 'LayoutDashboard',
                description: 'View athlete overview',
                keywords: ['athlete', 'overview'],
                action: () => context.navigate(`${baseUrl}?tab=overview`),
                roles: ['coach', 'admin']
            },
            {
                id: 'athlete-detail-recovery',
                label: 'Athlete: Recovery',
                icon: 'Heart',
                description: 'View recovery metrics',
                keywords: ['athlete', 'recovery'],
                action: () => context.navigate(`${baseUrl}?tab=recovery`),
                roles: ['coach', 'admin']
            },
            {
                id: 'athlete-detail-sleep',
                label: 'Athlete: Sleep',
                icon: 'Moon',
                description: 'View sleep analysis',
                keywords: ['athlete', 'sleep'],
                action: () => context.navigate(`${baseUrl}?tab=sleep`),
                roles: ['coach', 'admin']
            },
            {
                id: 'athlete-detail-activity',
                label: 'Athlete: Activity',
                icon: 'Flame',
                description: 'View activity data',
                keywords: ['athlete', 'activity'],
                action: () => context.navigate(`${baseUrl}?tab=activity`),
                roles: ['coach', 'admin']
            },
            {
                id: 'athlete-detail-ai-insights',
                label: 'Athlete: AI Insights',
                icon: 'Bot',
                description: 'View AI insights',
                keywords: ['athlete', 'ai'],
                action: () => context.navigate(`${baseUrl}?tab=ai-insights`),
                roles: ['coach', 'admin']
            }
        );
    }

    commands.push(
        {
            id: 'nav-dashboard',
            label: 'Go to Dashboard',
            icon: 'LayoutDashboard',
            description: 'View your dashboard',
            keywords: ['dashboard', 'home'],
            action: () => context.navigate('/dashboard'),
            roles: ['athlete', 'coach', 'admin']
        },
        {
            id: 'nav-team-info',
            label: 'Go to Team Information',
            icon: 'Info',
            description: 'View team details',
            keywords: ['team', 'info'],
            action: () => context.navigate('/team-information'),
            roles: ['coach', 'admin']
        },
        {
            id: 'nav-admin-dashboard',
            label: 'Go to Admin Dashboard',
            icon: 'Settings',
            description: 'Access admin controls',
            keywords: ['admin', 'dashboard'],
            action: () => context.navigate('/admin-dashboard'),
            roles: ['admin']
        },
        {
            id: 'nav-register',
            label: 'Register New User',
            icon: 'UserPlus',
            description: 'Create a new user account',
            keywords: ['register', 'signup'],
            action: () => context.navigate('/register'),
            roles: ['admin', 'coach', 'athlete']
        },
        {
            id: 'nav-connect-whoop',
            label: 'Connect Whoop Device',
            icon: 'Watch',
            description: 'Link your Whoop device',
            keywords: ['whoop', 'connect'],
            action: () => context.navigate('/connect-whoop'),
            roles: ['athlete', 'coach', 'admin']
        },
        {
            id: 'nav-connect-terra',
            label: 'Connect Device From Terra',
            icon: '🔗',
            description: 'Link your wearable device via Terra',
            keywords: ['terra', 'connect', 'device', 'wearable'],
            action: () => context.navigate('/connect-terra'),
            roles: ['athlete', 'coach', 'admin']
        },
        // Catapult Vector connection removed - not implementing yet
    );

    return commands;
};
